import React, { useId, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../hooks/useLanguage';

const ChatHistoryPanel = ({
  sessions = [],
  selectedId = null,
  onSelect,
  onNew,
  onRename,
  onDelete,
  onClose,
  loading = false,
  historyBusy = false,
  error = '',
  onRetry,
  closeOnDesktop = false,
  titleId,
  descriptionId,
  titleText,
  descriptionText,
  eyebrow,
  variant = 'default',
  formatSessionDate,
}) => {
  const { t, currentLanguage } = useLanguage();
  const [filter, setFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const editInputRef = useRef(null);
  const instanceId = useId().replace(/:/g, '');
  const headingId = titleId || `chat-history-title-${instanceId}`;
  const panelDescriptionId = descriptionId || `chat-history-description-${instanceId}`;
  const searchId = `conversation-search-${instanceId}`;
  const isContestVariant = variant === 'contest';
  const isBusy = loading || historyBusy;
  const hasFilter = Boolean(filter.trim());

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return sessions;
    return sessions.filter(s => `${s.title || ''} ${s.preview || ''}`.toLowerCase().includes(f));
  }, [filter, sessions]);

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditingTitle(s.title || `${t('chat.history.defaultTitle')} #${s.id}`);
    window.requestAnimationFrame(() => editInputRef.current?.focus());
  };

  const commitEdit = () => {
    if (editingId === null) return;
    const val = editingTitle.trim();
    if (val && onRename) onRename(editingId, val);
    cancelEdit();
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingTitle('');
  };

  const formatDate = value => {
    if (formatSessionDate) return formatSessionDate(value);
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(currentLanguage?.startsWith('en') ? 'en-US' : 'es-PE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };

  const messageCountLabel = count => t(
    count === 1 ? 'chat.history.messageCountSingular' : 'chat.history.messageCount',
    { count },
  );

  const renderEmptyState = () => (
    <div className="chat-history-empty">
      <span className="chat-history-empty__icon" aria-hidden="true">
        <i className={`bi ${hasFilter ? 'bi-search' : 'bi-journal-bookmark'}`} />
      </span>
      <strong>{t(hasFilter ? 'chat.history.noMatchesTitle' : 'chat.history.emptyTitle')}</strong>
      <p>{t(hasFilter ? 'chat.history.noMatchesDescription' : 'chat.history.emptyDescription')}</p>
      {hasFilter && (
        <button type="button" className="chat-history-empty__action" onClick={() => setFilter('')}>
          {t('chat.history.clearSearch')}
        </button>
      )}
    </div>
  );

  const renderLoadingState = () => (
    <div className="chat-history-loading" role="status" aria-label={t('chat.history.loading')}>
      {['one', 'two', 'three'].map(item => <span key={item} />)}
    </div>
  );

  const resultLabel = filtered.length === 1
    ? t('chat.history.resultCountSingular', { count: filtered.length })
    : t('chat.history.resultCount', { count: filtered.length });

  const panelClasses = [
    'card',
    'h-100',
    'shadow-sm',
    'chat-history-panel',
    isContestVariant ? 'chat-history-panel--contest' : '',
  ].filter(Boolean).join(' ');

  return (
    <section
      className={panelClasses}
      aria-labelledby={headingId}
      aria-describedby={descriptionText ? panelDescriptionId : undefined}
    >
      <header className="card-header chat-history-panel__header">
        <div className="chat-history-panel__heading">
          {eyebrow && <p className="chat-history-panel__eyebrow">{eyebrow}</p>}
          <h2 id={headingId}>{titleText || t('chat.history.title')}</h2>
          {descriptionText && <p id={panelDescriptionId}>{descriptionText}</p>}
        </div>
        <div className="chat-history-panel__header-actions">
          {onNew && (
            <button type="button" className="btn btn-sm btn-primary chat-history-new" onClick={onNew} disabled={isBusy} aria-label={t('chat.history.new')} title={t('chat.history.new')}>
              <i className="bi bi-plus-lg" aria-hidden="true" />
              <span>{t('chat.history.new')}</span>
            </button>
          )}
          {onClose && (
            <button
              type="button"
              className={`btn btn-sm btn-outline-secondary chat-history-close ${closeOnDesktop ? '' : 'd-md-none'}`}
              onClick={onClose}
              aria-label={t('chat.history.close')}
              title={t('chat.history.close')}
            >
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          )}
        </div>
      </header>
      <div className="card-body chat-history-panel__body">
        <div className="chat-history-panel__toolbar">
          <label htmlFor={searchId} className="visually-hidden">{t('chat.history.search')}</label>
          <div className="chat-history-search">
            <i className="bi bi-search" aria-hidden="true" />
            <input
              id={searchId}
              name={searchId}
              className="form-control form-control-sm"
              placeholder={t('chat.history.search')}
              value={filter}
              onChange={e => setFilter(e.target.value)}
              disabled={isBusy}
              autoComplete="off"
            />
            {hasFilter && (
              <button type="button" className="chat-history-search__clear" onClick={() => setFilter('')} aria-label={t('common.clear')}>
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            )}
          </div>
          {sessions.length > 0 && <span className="chat-history-result-count">{resultLabel}</span>}
        </div>

        {error && (
          <div className="chat-history-alert" role="alert">
            <i className="bi bi-exclamation-triangle" aria-hidden="true" />
            <span>{error}</span>
            {onRetry && <button type="button" onClick={onRetry} disabled={isBusy}>{t('chat.history.retry')}</button>}
          </div>
        )}

        <div className="chat-history-list-wrapper" aria-live="polite" aria-busy={isBusy}>
          {isBusy && sessions.length === 0 ? renderLoadingState() : filtered.length === 0 ? renderEmptyState() : (
            <ul className="list-group list-group-flush overflow-auto chat-history-list">
              {filtered.map(s => {
                const isActive = s.id === selectedId;
                const displayTitle = s.title || `${t('chat.history.defaultTitle')} #${s.id}`;
                const showPreview = s.preview && !(
                  displayTitle.startsWith(s.preview)
                  || s.preview.startsWith(displayTitle)
                );
                const editId = `conversation-title-${instanceId}-${s.id}`;
                const updatedAt = formatDate(s.updated_at || s.created_at);
                return (
                  <li
                    key={s.id}
                    className={`list-group-item chat-history-item d-flex align-items-start justify-content-between ${isActive ? 'active text-white chat-history-item--active' : ''}`}
                  >
                    <div className="chat-history-item__content">
                      {editingId === s.id ? (
                        <div className="chat-history-editing">
                          <label htmlFor={editId} className="visually-hidden">{t('chat.history.rename')} {displayTitle}</label>
                          <input
                            id={editId}
                            ref={editInputRef}
                            className="form-control form-control-sm"
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onBlur={commitEdit}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') commitEdit();
                              if (e.key === 'Escape') {
                                e.preventDefault();
                                e.stopPropagation();
                                cancelEdit();
                              }
                            }}
                            maxLength={120}
                            disabled={isBusy}
                          />
                          <div className="chat-history-editing__actions">
                            <button type="button" className="btn btn-sm btn-primary" onClick={commitEdit} disabled={isBusy || !editingTitle.trim()}>{t('common.save')}</button>
                            <button type="button" className="btn btn-sm btn-link" onClick={cancelEdit} disabled={isBusy}>{t('common.cancel')}</button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="history-session-button"
                          onClick={() => onSelect && onSelect(s.id)}
                          aria-current={isActive ? 'true' : undefined}
                          disabled={isBusy}
                        >
                          <span className="chat-history-item__title text-truncate" title={displayTitle}>{displayTitle}</span>
                          {showPreview && <span className={`history-preview ${isActive ? 'text-white-75' : ''}`}>{s.preview}</span>}
                        </button>
                      )}
                      <div className={`small history-message-count chat-history-item__meta ${isActive ? 'text-white-75' : 'text-muted'}`}>
                        <span>{messageCountLabel(s.messages_count ?? 0)}</span>
                        {updatedAt && <span>{t('chat.history.updatedAt', { date: updatedAt })}</span>}
                      </div>
                    </div>
                    <div className="history-actions chat-history-item__actions">
                      {onRename && editingId !== s.id && (
                        <button
                          type="button"
                          className={`btn btn-sm ${isActive ? 'btn-light' : 'btn-outline-secondary'}`}
                          onClick={() => startEdit(s)}
                          aria-label={`${t('chat.history.rename')} ${displayTitle}`}
                          title={t('chat.history.rename')}
                          disabled={isBusy}
                        >
                          <i className="bi bi-pencil" aria-hidden="true" />
                        </button>
                      )}
                      {onDelete && (
                        <button
                          type="button"
                          className={`btn btn-sm ${isActive ? 'btn-light' : 'btn-outline-secondary'}`}
                          onClick={() => onDelete(s.id)}
                          aria-label={`${t('chat.history.delete')} ${displayTitle}`}
                          title={t('chat.history.delete')}
                          disabled={isBusy}
                        >
                          <i className="bi bi-trash" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
};

export default ChatHistoryPanel;
